import { ViewConfig } from "@lib/views";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { AMOUNT_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import { EliminationDetailJournalEntry } from "@common/schemas/domains/accounting/elimination.schema";
import {
    EliminationAmountCell,
    EliminationBalanceCell,
    EliminationClassCell,
    EliminationCounterpartyCell,
    EliminationDescriptionCell,
    EliminationEntityCell,
    EliminationNameCell,
    EliminationReferenceCell,
    EliminationSourceCell,
    EliminationSplitCell,
} from "./eliminationDetailCells";

export enum EliminationDetailColumn {
    NAME = "name",
    ENTITY = "entity",
    SOURCE = "source",
    REFERENCE = "reference",
    DESCRIPTION = "description",
    COUNTERPARTY = "counterparty",
    CLASS = "class",
    SPLIT = "split",
    AMOUNT = "amount",
    BALANCE = "balance",
}

export type EliminationDetailRowKind =
    | "account"
    | "openingBalance"
    | "transaction"
    | "closingBalance";

// Single row shape across the nested table (mirrors the general ledger's
// GLViewRow): fields are populated per `rowKind`.
export interface EliminationDetailViewRow {
    id: string;
    rowKind: EliminationDetailRowKind;
    children?: EliminationDetailViewRow[];
    // "account" rows
    accountLabel?: string;
    closingBalance?: number;
    // "openingBalance" / "closingBalance" rows
    balanceValue?: number;
    // "transaction" rows
    entityId?: number;
    entityName?: string;
    origin?: "engine" | "manual";
    manualJournalEntryId?: string;
    date?: string;
    counterpartyName?: string | null;
    journalEntries?: EliminationDetailJournalEntry[];
    description?: string;
    split?: string;
    amount?: number;
    balance?: number;
}

export interface EliminationDetailTableCallbacks {
    onOpenEliminatingEntry?: (manualJournalEntryId: string) => void;
    canOpenEliminatingEntry?: (manualJournalEntryId: string) => boolean;
}

export type EliminationDetailTableMeta = ViewTableMeta<
    EliminationDetailTableCallbacks,
    never
>;

export const eliminationDetailViewConfig: ViewConfig<
    EliminationDetailViewRow,
    EliminationDetailColumn,
    never
> = {
    columns: {
        [EliminationDetailColumn.NAME]: {
            accessorKey: "rowKind",
            name: "Account / Date",
            header: "Date",
            cell: EliminationNameCell,
            minSize: 300,
            maxSize: 480,
            immutable: true,
        },
        [EliminationDetailColumn.ENTITY]: {
            accessorKey: "entityName",
            name: "Entity",
            header: "Entity",
            cell: EliminationEntityCell,
            minSize: 100,
            maxSize: 100,
        },
        [EliminationDetailColumn.SOURCE]: {
            accessorKey: "origin",
            name: "Source",
            header: "Source",
            cell: EliminationSourceCell,
            minSize: 100,
            maxSize: 160,
        },
        [EliminationDetailColumn.REFERENCE]: {
            accessorKey: "journalEntries",
            name: "Reference",
            header: "Reference",
            cell: EliminationReferenceCell,
            minSize: 160,
            maxSize: 260,
        },
        [EliminationDetailColumn.DESCRIPTION]: {
            accessorKey: "description",
            name: "Description",
            header: "Description",
            cell: EliminationDescriptionCell,
            minSize: 200,
            maxSize: 360,
        },
        [EliminationDetailColumn.COUNTERPARTY]: {
            accessorKey: "counterpartyName",
            name: "Counterparty",
            header: "Counterparty",
            cell: EliminationCounterpartyCell,
            minSize: 140,
            maxSize: 220,
        },
        [EliminationDetailColumn.CLASS]: {
            accessorKey: "id",
            name: "Class",
            header: "Class",
            cell: EliminationClassCell,
            minSize: 100,
            maxSize: 200,
        },
        [EliminationDetailColumn.SPLIT]: {
            accessorKey: "split",
            name: "Split",
            header: "Split",
            cell: EliminationSplitCell,
            minSize: 100,
            maxSize: 200,
        },
        [EliminationDetailColumn.AMOUNT]: {
            accessorKey: "amount",
            name: "Amount",
            header: "Amount",
            cell: EliminationAmountCell,
            ...AMOUNT_COLUMN_SIZE,
            immutable: true,
        },
        [EliminationDetailColumn.BALANCE]: {
            accessorKey: "balance",
            name: "Balance",
            header: "Balance",
            cell: EliminationBalanceCell,
            ...AMOUNT_COLUMN_SIZE,
            immutable: true,
        },
    },
    nesting: {
        enabled: true,
        expandColumn: EliminationDetailColumn.NAME,
        childrenKey: "children",
    },
};
